import React, { ReactNode } from 'react'
import {
    Badge,
    Box,
    CloseButton,
    Flex,
    BoxProps,
    Drawer,
    DrawerContent,
    useDisclosure,
    Heading
} from '@chakra-ui/react'
import {
    FiSettings,
    FiUsers,
    FiLogOut,
    FiHome,
    FiUserPlus,
    FiFileText,
    FiRefreshCw
} from 'react-icons/fi'
import { PiChalkboardTeacher, PiCopy } from 'react-icons/pi'
import { MdOutlineDocumentScanner } from 'react-icons/md'
import { FaPersonWalkingArrowRight } from 'react-icons/fa6'
import { useLocation } from 'react-router-dom'
import { IconType } from 'react-icons'

import logo from '@/assets/TheLordHeritage_logo.png'

const LOGO_DIMENSION = 120
import NavItem from './NavItem'
import MobileNav from './MobileNav'
import AppVersion from './AppVersion'
import { useAuth } from '@/shared/context/AuthContext'
import { useUserTypes } from '@/shared/hooks/useUserTypes'
import { UserTypes } from '@/shared/types/auth'
import { canManageContent } from '@/shared/api/rest/adapters'
import { useNotifications } from '@/shared/hooks/useNotifications'
import { PiSparkle } from 'react-icons/pi'

type LinkItemProps = {
    name: string
    icon: IconType
    href: string
    isActive?: boolean
}

const TeacherLinkItems: Array<LinkItemProps> = [
    {
        name: 'Check In/Out',
        icon: MdOutlineDocumentScanner,
        href: '/user/teacher/check-in-out'
    },
    {
        name: 'Queue',
        icon: FaPersonWalkingArrowRight,
        href: '/user/teacher/queue'
    },
    { name: 'Report', icon: FiFileText, href: '/user/teacher/teacher-report' }
]

// "Children" is shown to every signed-in user — adding a child auto-creates a
// parent record on the backend, so a teacher who now has a child can register
// them in one step (mirrors the mobile app).
const ChildrenLinkItem: LinkItemProps = {
    name: 'Children',
    icon: FiUsers,
    href: '/user/parent/children-list'
}

// Co-Parent / Guardian manage relationships around existing children, so they
// stay gated behind the parent role.
const ParentLinkItems: Array<LinkItemProps> = [
    {
        name: 'Co-Parent',
        icon: FiRefreshCw,
        href: '/user/parent/coparent'
    },
    {
        name: 'Guardian', // "Guardian" is being used to discourage parent from double entry given they can add spouse as "co-parent"
        icon: FiUserPlus,
        href: '/user/parent/alternative-contact'
    }
]

type Props = {
    children: ReactNode
}

export default function SideNav({ children }: Props) {
    const { isOpen, onOpen, onClose } = useDisclosure()
    return (
        <Box minH="100vh" bg={'gray.100'}>
            <SidebarContent
                onClose={() => onClose}
                display={{ base: 'none', md: 'flex' }}
            />
            <Drawer
                autoFocus={false}
                isOpen={isOpen}
                placement="left"
                onClose={onClose}
                returnFocusOnClose={false}
                onOverlayClick={onClose}
            >
                <DrawerContent>
                    <SidebarContent onClose={onClose} />
                </DrawerContent>
            </Drawer>
            {/* mobilenav */}
            <MobileNav onOpen={onOpen} />
            <Box ml={{ base: 0, md: 60 }} p="4">
                {children}
            </Box>
        </Box>
    )
}

export interface SidebarProps extends BoxProps {
    onClose: () => void
}

const SidebarContent = ({ onClose, ...rest }: SidebarProps) => {
    const { logout, user } = useAuth()
    const { pathname } = useLocation()
    const userTypes = useUserTypes()
    const isParent = userTypes.hasType(UserTypes.IS_PARENT)
    const canManageKidsContent = canManageContent(user?.permissions)
    // Mirror PROTECTED_ROUTES in shared/config/routes.ts: global admins (sudo)
    // and admin teachers may access the teacher pages, so the sidebar must
    // surface those links to them too.
    const isTeacher = userTypes.hasAnyType([
        UserTypes.IS_TEACHER,
        UserTypes.IS_ADMIN_TEACHER,
        UserTypes.IS_GLOBAL_ADMIN
    ])
    const isAdminTeacher = userTypes.hasAnyType([
        UserTypes.IS_ADMIN_TEACHER,
        UserTypes.IS_GLOBAL_ADMIN
    ])
    // Same derived feed as the header bell. The nav badges count every waiting
    // item, not just unread ones — marking a notification read shouldn't hide
    // work that is still outstanding.
    const { notifications } = useNotifications()
    const pendingCount = notifications.filter(
        (notification) => notification.type === 'teacher-request'
    ).length
    const duplicateCount = notifications.filter(
        (notification) => notification.type === 'duplicate-parent'
    ).length

    return (
        <Box
            transition="3s ease"
            bg={'white'}
            borderRight="1px"
            borderRightColor={'gray.100'}
            w={{ base: 'full', md: 60 }}
            pos="fixed"
            h="full"
            overflowY="scroll"
            display="flex"
            flexDirection="column"
            // A column so the build stamp can sit against the bottom edge
            // (mt="auto") however short this user's nav is. Column flex would
            // otherwise squash the nav items to fit once the list is long
            // enough to scroll, so every direct child keeps its natural height.
            sx={{ '& > *': { flexShrink: 0 } }}
            {...rest}
        >
            <Flex
                h="48"
                pt="4"
                alignItems="center"
                mx="8"
                mb="4"
                justifyContent="space-between"
            >
                <img
                    src={logo}
                    alt="app logo"
                    width={LOGO_DIMENSION}
                    height={LOGO_DIMENSION}
                />
                <CloseButton
                    bg="brand.50"
                    size="md"
                    display={{ base: 'flex', md: 'none' }}
                    onClick={onClose}
                />
            </Flex>
            <Heading
                fontSize="small"
                p="4"
                mx="2"
                color={'brand.200'}
                textTransform={'uppercase'}
            >
                Parent
            </Heading>
            <NavItem
                isActive={pathname === '/user/dashboard'}
                icon={FiHome}
                href={'/user/dashboard'}
                aria-current={pathname === '/user/dashboard'}
                onClick={onClose}
            >
                Dashboard
            </NavItem>
            <NavItem
                isActive={pathname === ChildrenLinkItem.href}
                icon={ChildrenLinkItem.icon}
                href={ChildrenLinkItem.href}
                aria-current={pathname === ChildrenLinkItem.href}
                onClick={onClose}
            >
                {ChildrenLinkItem.name}
            </NavItem>
            {/* Only surfaced once a duplicate is actually waiting — there is
                nothing to see on this page otherwise. Admin teachers review
                duplicates from Duplicate Registration below instead. */}
            {!isAdminTeacher && duplicateCount > 0 && (
                <NavItem
                    isActive={pathname === '/user/parent/duplicates'}
                    icon={PiCopy}
                    href={'/user/parent/duplicates'}
                    aria-current={pathname === '/user/parent/duplicates'}
                    onClick={onClose}
                >
                    <Flex align="center" gap={2}>
                        Duplicate Account
                        <Badge
                            colorScheme="red"
                            borderRadius="full"
                            px={2}
                            fontSize="10px"
                        >
                            {duplicateCount}
                        </Badge>
                    </Flex>
                </NavItem>
            )}
            {isParent &&
                ParentLinkItems.map((link: LinkItemProps) => (
                    <NavItem
                        isActive={pathname === link.href}
                        key={link.name}
                        icon={link.icon}
                        href={link.href}
                        aria-current={pathname === link.href}
                        onClick={onClose}
                    >
                        {link.name}
                    </NavItem>
                ))}
            {isTeacher && (
                <Heading
                    fontSize="small"
                    p="4"
                    mx="2"
                    color={'brand.200'}
                    textTransform={'uppercase'}
                >
                    Teacher
                </Heading>
            )}
            {isTeacher &&
                TeacherLinkItems.map((link: LinkItemProps) => (
                    <NavItem
                        isActive={pathname === link.href}
                        key={link.name}
                        icon={link.icon}
                        href={link.href}
                        aria-current={pathname === link.href}
                        onClick={onClose}
                    >
                        {link.name}
                    </NavItem>
                ))}

            {isAdminTeacher && (
                <>
                    <NavItem
                        isActive={pathname === '/user/teacher/parents'}
                        icon={FiUsers}
                        href={'/user/teacher/parents'}
                        aria-current={pathname === '/user/teacher/parents'}
                        onClick={onClose}
                    >
                        Parent
                    </NavItem>
                    {/* Pending approvals live here now, behind the "Pending"
                        status filter — the separate page has been retired. */}
                    <NavItem
                        isActive={pathname === '/user/teacher/teachers'}
                        icon={PiChalkboardTeacher}
                        href={'/user/teacher/teachers'}
                        aria-current={pathname === '/user/teacher/teachers'}
                        onClick={onClose}
                    >
                        <Flex align="center" gap={2}>
                            Teachers
                            {pendingCount > 0 ? (
                                <Badge
                                    colorScheme="red"
                                    borderRadius="full"
                                    px={2}
                                    fontSize="10px"
                                >
                                    {pendingCount}
                                </Badge>
                            ) : null}
                        </Flex>
                    </NavItem>
                    <NavItem
                        isActive={pathname === '/user/teacher/duplicate-registration'}
                        icon={PiCopy}
                        href={'/user/teacher/duplicate-registration'}
                        aria-current={pathname === '/user/teacher/duplicate-registration'}
                        onClick={onClose}
                    >
                        <Flex align="center" gap={2}>
                            Duplicates
                            {duplicateCount > 0 ? (
                                <Badge
                                    colorScheme="red"
                                    borderRadius="full"
                                    px={2}
                                    fontSize="10px"
                                >
                                    {duplicateCount}
                                </Badge>
                            ) : null}
                        </Flex>
                    </NavItem>
                </>
            )}
            {canManageKidsContent && (
                <>
                    <Heading
                        fontSize="small"
                        p="4"
                        mx="2"
                        color={'brand.200'}
                        textTransform={'uppercase'}
                    >
                        Content
                    </Heading>
                    <NavItem
                        isActive={pathname.startsWith('/admin')}
                        icon={PiSparkle}
                        href={'/admin'}
                        aria-current={pathname.startsWith('/admin')}
                        onClick={onClose}
                    >
                        Anointed Kids
                    </NavItem>
                </>
            )}
            <NavItem
                isActive={pathname === '/user/settings'}
                icon={FiSettings}
                href={'/user/settings'}
                aria-current={pathname === '/user/settings'}
                onClick={onClose}
            >
                Settings
            </NavItem>
            <NavItem href="" mt="8" color="red" icon={FiLogOut} onClick={logout}>
                Logout
            </NavItem>
            <AppVersion mt="auto" />
        </Box>
    )
}
